export default function Privacy() {
  const updated = "MAY 2026";
  return (
    <div
      className="max-w-[900px] mx-auto px-6 md:px-10 py-14 md:py-20"
      data-testid="privacy-page"
    >
      <div className="micro-label">/ Privacy Policy</div>
      <h1 className="font-display font-black tracking-tight leading-[0.95] text-4xl md:text-5xl mt-3">
        Privacy Policy
      </h1>
      <p className="text-xs font-mono text-[var(--fg-2)] mt-3 tracking-widest">
        LAST UPDATED · {updated} · ALIGNED WITH POPIA
      </p>

      <div className="mt-10 space-y-8 text-sm leading-relaxed text-[var(--fg-2)]">
        <p>
          Decal Labs ("we", "us") is a South African business that respects your
          privacy and the Protection of Personal Information Act (POPIA). This
          policy explains, in plain language, what personal information we
          collect, why we collect it, and the choices you have about it.
        </p>

        <Section title="1. What information we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>Account information</strong> — your email address and a
              securely hashed password when you create an account.
            </li>
            <li>
              <strong>Billing information</strong> — handled by our payment
              processors (Peach Payments / PayFast). We only see the transaction
              ID, plan, amount, and outcome. We never see your card details.
            </li>
            <li>
              <strong>Uploaded files</strong> — the images and SVGs you upload
              are processed in memory for conversion purposes only. They are
              discarded immediately after the response, unless you choose to
              save them to your account.
            </li>
            <li>
              <strong>Usage telemetry</strong> — basic, non-identifying logs
              (conversion mode, output size, success / failure) so we can keep
              the engine healthy. We do not log the contents of your files.
            </li>
            <li>
              <strong>Anti-abuse tokens</strong> — to prevent free-trial abuse
              we store an irreversible SHA-256 hash of your IP and browser
              fingerprint. We do not store the raw IP address.
            </li>
          </ul>
        </Section>

        <Section title="2. Why we collect it (lawful basis)">
          <p>
            We collect this information on the lawful bases set out in POPIA:
            to provide the service you signed up for (contract performance),
            to charge for paid plans (legitimate interest), to keep the service
            secure (legitimate interest), and to comply with any legal
            obligations.
          </p>
        </Section>

        <Section title="3. How files are processed">
          <p>
            Your image is sent to our server, traced and optimised into an SVG,
            and the result returned to your browser. The original upload is
            then discarded. We do not claim any ownership of your artwork — it
            remains yours. You remain responsible for ensuring you have the
            rights to upload, convert, and use the source artwork.
          </p>
        </Section>

        <Section title="4. Billing & payment handling">
          <p>
            Subscriptions and credit packs are processed by Peach Payments and
            PayFast (POPIA-compliant gateways). We receive only the
            transaction outcome and a tokenised reference. Card numbers and
            CVV codes never reach our servers.
          </p>
        </Section>

        <Section title="5. Cookies">
          <p>We use two strictly necessary cookies:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1.5">
            <li>
              <span className="font-mono text-[var(--fg)]">access_token</span>{" "}
              — keeps you signed in.
            </li>
            <li>
              <span className="font-mono text-[var(--fg)]">decal_anon</span>{" "}
              — preserves your free-trial credits between visits.
            </li>
          </ul>
          <p className="mt-2">
            We do not use third-party advertising cookies, social media
            beacons, or behavioural tracking pixels.
          </p>
        </Section>

        <Section title="6. Account security">
          <p>
            Passwords are hashed with bcrypt. Sessions are signed with rotating
            secrets. We rate-limit login attempts and lock accounts after
            repeated failures. Authentication events are kept for 90 days for
            forensic review.
          </p>
        </Section>

        <Section title="7. Your rights under POPIA">
          <p>
            You have the right to access the personal information we hold about
            you, to correct it, to object to processing, and to request
            deletion of your account and all associated data. To exercise any
            of these rights, email{" "}
            <a
              href="mailto:support@decallabs.co.za"
              className="underline text-[var(--fg)]"
            >
              support@decallabs.co.za
            </a>{" "}
            from your account email address. We will respond within 30 days.
          </p>
        </Section>

        <Section title="8. Data retention">
          <p>
            Account data is retained for as long as your account is active.
            Saved conversions remain until you delete them or close your
            account. Billing records are retained for 5 years to satisfy
            South African tax obligations. Anti-abuse hashes are rotated every
            12 months.
          </p>
        </Section>

        <Section title="9. Third-party services">
          <p>
            We rely on a small number of trusted third parties: Peach Payments
            and PayFast for billing, our hosting provider for compute and
            storage, and our transactional email provider. None of them have
            permission to use your data for their own purposes.
          </p>
        </Section>

        <Section title="10. We do not sell your data">
          <p>
            We never sell, rent, or trade personal information. We never use
            uploaded artwork for marketing or training without explicit,
            opt-in consent.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            For any privacy question, complaint, or data-subject request,
            email{" "}
            <a
              href="mailto:support@decallabs.co.za"
              className="underline text-[var(--fg)]"
            >
              support@decallabs.co.za
            </a>
            . You may also lodge a complaint with the South African
            Information Regulator if you believe your rights under POPIA have
            been breached.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-[var(--fg)]">{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}
